import type { ConceptDefinitionDTO } from "@eu.mdotm.api/smart-workspaces";

/**
 * The leaf that means "do not collapse this level — give me every category of it".
 *
 * A breakdown is not a second question asked next to the chain: it is an answer *inside* it. Where the
 * chain would normally pin a level ("currency = EUR"), picking this leaf instead asks the backend to
 * return one entry per category of that level, which is exactly what a stacked column draws. Two
 * consequences fall out of putting it here rather than in a dedicated select:
 *
 * - a level cannot be pinned *and* broken down, because they are the same slot;
 * - everything picked before it stays the pin ("currency exposure within equity").
 *
 * The sentinel never reaches the wire. `WorkspaceChartChain` translates it into
 * {@link WorkspaceChain.breakdown} and leaves `parameters` alone.
 */
export const breakdownAllOption = "__ALL__";

/** Labels for the risk-model levels, keyed the way `RiskModelExposureLevels` keys them. */
export const riskModelLevelLabels = {
	macroAssetClass: "Macro Asset Class",
	microAssetClass: "Micro Asset Class",
	geography: "Macro Geography",
	microGeography: "Micro Geography",
	currency: "Currency",
};

type RiskModelLevel = keyof typeof riskModelLevelLabels;

/**
 * The risk model's levels and its breakdown dimensions are the same five things under two different
 * names: `RiskModelExposureLevels` keys them in camelCase, the backend advertises and accepts
 * SCREAMING_SNAKE identifiers. Note `geography` ↔ `MACRO_GEOGRAPHY` — the one pair where the two
 * vocabularies disagree on more than case, and the reason this is a table rather than a `toUpperCase`.
 */
const riskModelDimensionByLevel: Record<RiskModelLevel, string> = {
	macroAssetClass: "MACRO_ASSET_CLASS",
	microAssetClass: "MICRO_ASSET_CLASS",
	geography: "MACRO_GEOGRAPHY",
	microGeography: "MICRO_GEOGRAPHY",
	currency: "CURRENCY",
};

/** The identifier to send for a risk-model level, or `undefined` if it is not one. */
export function riskModelDimensionForLevel(level: string): string | undefined {
	return riskModelDimensionByLevel[level as RiskModelLevel];
}

/** The level a persisted dimension belongs to, or `undefined` if it is not a risk-model dimension. */
export function riskModelLevelForDimension(dimension: string): RiskModelLevel | undefined {
	return (Object.keys(riskModelDimensionByLevel) as Array<RiskModelLevel>).find(
		(level) => riskModelDimensionByLevel[level] === dimension,
	);
}

/**
 * How a dimension identifier is written for humans.
 *
 * A tag concept's dimension is an opaque backend identifier, so the concept it came from is the only
 * place its name exists — pass it whenever you have it.
 */
export function breakdownDimensionLabel(dimension: string, concept?: ConceptDefinitionDTO): string {
	const level = riskModelLevelForDimension(dimension);
	return level ? riskModelLevelLabels[level] : (concept?.defaultLabel ?? humanizeDimension(dimension));
}

/** Last resort for a dimension with no known name: `"issuerCountry"` → `"Issuer country"`. */
function humanizeDimension(dimension: string): string {
	const spaced = dimension.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * The dimension a tag concept is broken down by — the tag itself.
 *
 * A tag exposure has exactly one axis (its own options), so the concept declares at most one dimension
 * and the identifier is whatever the backend chose to call it — no translation table applies here, and
 * none should be invented. `undefined` means the tag cannot be broken down, so no "All" leaf.
 */
export function tagBreakdownDimension(concept: ConceptDefinitionDTO | undefined): string | undefined {
	return concept?.breakdownDimensions?.at(0);
}
